/** @jsx jsx */
import { useState, useContext, useEffect } from 'react'
import { css, jsx } from '@emotion/core'
import { differenceInHours } from 'date-fns'
import { Select, Loader } from '@livechat/design-system'

import { Context } from '../../context'
import api from '../../api'
import { SummaryReport, DetailedReport } from '../../components'

import chartIcon from '../../assets/icons/bar_chart.svg'
import listIcon from '../../assets/icons/list-black.svg'

import { checkLocalStorage } from '../../utils'

const icons = {
  summary: chartIcon,
  detailed: listIcon,
}

const mainCss = css`
  width: calc(100vw - 200px);
  height: calc(100vh - 50px);
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 855px) {
    width: 100vw;
    height: calc(100vh - 50px);
  }

  .select-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    > img {
      width: 100px;
      margin-top: 250px;
      margin-bottom: 10px;
    }
  }

  .select-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
    width: 298px;
    height: 28px;
    border-radius: 4px;
    padding: 5px 0;

    > span {
      margin-left: 10px;
      font-weight: 100;
    }
  }

  .report-manager {
    margin: 20px 0 16px 0;
    display: flex;
    align-items: center;

    @media (max-width: 640px) {
      flex-direction: column;
    }

    @media (max-width: 980px) {
      align-items: start;
      margin: 20px 0 0px 0;

      > span {
        margin-bottom: 10px;
      }

      .select-type {
        margin-bottom: 20px;
      }
    }
  }

  .label {
    margin-right: 10px;
    margin-top: 2px;
    font-weight: 100;

    @media (max-width: 980px) {
      display: none;
    }
  }

  .select-survey {
    width: 220px;
  }

  .select-type {
    width: 220px;
    margin-right: 10px;
  }

  .number {
    font-weight: bold;
  }

  .pie {
    width: 200px;
  }

  .item-body {
    display: flex;
    align-items: center;
  }

  .icon {
    margin-top: 1px;
    margin-right: 5px;
  }
`

const Feedback = () => {
  const {
    surveys,
    surveyToken,
    surveysLoading,
    setSurveysLoading,
    surveyResponse,
    setSurveyResponse,
    surveyFeedback,
    setSurveyFeedback,
    feedbackType,
    setFeedbackType,
  } = useContext(Context)

  const [structure, setStructure] = useState(null)
  const [responseLoading, setResponseLoading] = useState(false)
  const [structureLoading, setStructureLoading] = useState(false)
  const [isSelectDisabled, setSelectDisabled] = useState(false)

  useEffect(() => {
    const request = async () => {
      setResponseLoading(true)

      let shouldUpdate = false
      let localResponse

      if (checkLocalStorage()) {
        localResponse = localStorage.getItem(`response/${surveyFeedback}`)
      }

      if (localResponse) {
        const updatedAt = JSON.parse(localResponse).updatedAt

        if (differenceInHours(new Date(), new Date(updatedAt)) > 3) {
          shouldUpdate = true
        } else {
          setSurveyResponse(JSON.parse(localResponse).data)
        }
      } else {
        shouldUpdate = true
      }

      if (shouldUpdate) {
        await updateFeedback()
      }

      setResponseLoading(false)
    }

    if (surveyFeedback) {
      request()
    }
  }, [surveyFeedback])

  const updateFeedback = async () => {
    const remoteResponse = await api.getResponse(surveyFeedback, surveyToken)

    if (checkLocalStorage()) {
      localStorage.setItem(
        `response/${surveyFeedback}`,
        JSON.stringify({
          data: remoteResponse,
          updatedAt: new Date(),
        })
      )
    }

    setSurveyResponse(remoteResponse)
  }

  const types = surveys.map((el) => ({
    key: el.id,
    props: { name: el.name, value: el.id },
  }))

  useEffect(() => {
    if (surveyFeedback) {
      try {
        setStructureLoading(true)
        const currentStructure = surveys.find(
          (el) => el.id === surveyFeedback
        )?.structure

        setStructure(
          typeof currentStructure === 'string'
            ? JSON.parse(currentStructure)
            : currentStructure
        )
        setStructureLoading(false)
      } catch (err) {
        console.log(err)
      }
    } else {
      setStructure(null)
    }
  }, [surveyFeedback])

  const getItemBody = (props) => {
    if (!props) {
      return null
    }
    return (
      <div id={props.value} className="item-body">
        {icons[props.name] && (
          <img className="icon" src={icons[props.name]} alt={props.name} />
        )}
        <span>{props.name}</span>
      </div>
    )
  }

  const getSelectedItemBody = (props) => {
    return (
      <div id={props.value} className="item-body">
        {icons[props.name] && (
          <img className="icon" src={icons[props.name]} alt={props.name} />
        )}
        <span>{props.name}</span>
      </div>
    )
  }
  const showResults = !!(surveyFeedback && feedbackType && structure)
  const isLoading = responseLoading || structureLoading
  const surveyName = surveys?.find((el) => el.id === surveyFeedback)?.name

  return (
    <div css={mainCss}>
      <div className="select-container">
        {surveysLoading && !surveyFeedback && !feedbackType ? (
          <div className="select-loader">
            <Loader />
            <span>Loading customers feedback</span>
          </div>
        ) : (
          <div className="report-manager">
            <span className="label">Present a</span>
            <Select
              items={[
                {
                  key: 'summary',
                  props: { name: 'summary', value: 'summary' },
                },
                {
                  key: 'detailed',
                  props: { name: 'detailed', value: 'detailed' },
                },
              ]}
              onItemSelect={(id) => {
                setFeedbackType(id)
              }}
              getItemBody={getItemBody}
              placeholder="Select an report type"
              getSelectedItemBody={getSelectedItemBody}
              selected={feedbackType}
              className="select-type"
              disabled={isSelectDisabled || isLoading}
            />
            <span className="label">report for</span>
            <Select
              items={types}
              onItemSelect={(id) => {
                setSurveyFeedback(id)
              }}
              getItemBody={getItemBody}
              placeholder="Select an survey"
              getSelectedItemBody={getSelectedItemBody}
              selected={surveyFeedback}
              className="select-survey"
              disabled={isSelectDisabled || isLoading}
            />
          </div>
        )}
      </div>

      {showResults && feedbackType === 'summary' && (
        <SummaryReport
          key="summary-report"
          surveyName={surveyName}
          structure={structure}
          surveyId={surveyFeedback}
          response={surveyResponse}
          isLoading={isLoading}
          updateFeeback={updateFeedback}
          setSelectDisabled={setSelectDisabled}
          setSurveysLoading={setSurveysLoading}
        />
      )}
      {showResults && feedbackType === 'detailed' && (
        <DetailedReport
          key="detailed-report"
          surveyName={surveyName}
          structure={structure}
          surveyId={surveyFeedback}
          response={surveyResponse}
          isLoading={isLoading}
          updateFeeback={updateFeedback}
          setSelectDisabled={setSelectDisabled}
          setSurveysLoading={setSurveysLoading}
        />
      )}
    </div>
  )
}

export default Feedback
